import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../components/Icon';
import styles from './VideoHeader.module.scss';
import DotsBtn from '../../../components/Menu/DotsBtn';
import Menu from '../../../components/Menu/Menu';
import MenuItem from '../../../components/Menu/MenuItem';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { CustomButton } from '../../../components/Button/Button';
import { toast } from 'react-toastify';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { approvalStatuses } from '../../../features/Dashboard/components/VideosFilter/constants';
import { useUserContext } from '../../../context/UserContext';
import { CustomButtonTooltip } from '../../../components/ButtonTooltip/ButtonTooltip';
import { ToastNotification } from '../../../../engage/components/Toast/Toast';
import { DownloadUI } from '../../../../engage/components/Toast/DownloadUI';
import AnalyticsInfo from '../../../components/AnalyticsInfo/AnalyticsInfo';
import Hint from '../../../components/Hint/Hint';
import { MAX_NAME_LENGTH } from '../../../../common/constants/common';
import Tooltip from '../../../components/Tooltip/Tooltip';
import HoverExpandText from '../../../features/VideoDetails/components/HoverExpandText/HoverExpandText';
import RadioButton from '../../../components/RadioButton/RadioButton';
import { EDITED, ORIGINAL, VIDEO_VERSION } from '../../../features/Dashboard/const';
import CloseButton from '../../../components/CloseButton/CloseButton';
import Modal from '../../../components/Modal';

export const VideoHeader = ({
  sharedMode = false,
  shareVideo,
  handleCopyClick,
  moveVideo,
  archiveVideo,
  video,
  deleteVideo,
  duplicateVideo,
  openSubmitForReviewModal,
  isUserHasEditingRights,
  downloadVideo,
  canDownload,
  canViewAnalytics,
  canShare,
  analyticsInfo,
  isVideoProcessing,
  downloadNotification,
  draft,
  editable,
  editableRef,
  handleNameChange,
  size,
  handleOpenMobileMenu,
  onEditClick,
  isVideoAuthor,
  isExternallyEdited,
}) => {
  const { isApprovalFlowEnabled, isReviewerUser, isViewAnalyticsEnabled } = useUserContext();

  const [showShareMenu, setShowShareMenu] = useState({});
  const [showAnalyticsInfo, setShowAnalyticsInfo] = useState(false);
  const [downloadProgress, updateDownloadProgress] = useState(0);
  const [isPortrait, setIsPortrait] = useState(false);
  const [showDownloadMessage, setShowDownloadMessage] = useState(false);
  const [isNameHover, setIsNameHover] = useState(false);
  const [showChooseSourceModal, setShowChooseSourceModal] = useState(null);
  const [sourceMode, setSourceMode] = useState(EDITED);

  const toastId = useRef(null);

  const shareMenuRef = useRef(null);

  const analyticsMenuRef = useRef(null);

  const nameRef = useRef();

  const isProtected = useMemo(() => video.isProtected, [video]);

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const { t } = useTranslation();

  const companyLogo = useMemo(() => (video?.appConfig?.web_logo ? video?.appConfig?.web_logo?.path : null), [video]);

  const isActiveLogo = useMemo(() => {
    return companyLogo && (sharedMode ? true : !isApprovalFlowEnabled || isReviewerUser || isPreExistedStatus);
  }, [companyLogo, sharedMode, isApprovalFlowEnabled, isReviewerUser, isPreExistedStatus]);

  const isPreExistedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.PRE_EXISTED.TYPE, [video]);
  const isConceptStatus = useMemo(() => video?.approvalStatus === approvalStatuses.CONCEPT.TYPE, [video]);
  const isInReviewStatus = useMemo(() => video?.approvalStatus === approvalStatuses.IN_REVIEW.TYPE, [video]);
  const isDeclinedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.DECLINED.TYPE, [video]);
  const isApprovedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.APPROVED.TYPE, [video]);

  const shareAllowed = useMemo(() => {
    if (sharedMode) {
      return canShare;
    } else {
      return !isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus;
    }
  }, [sharedMode, canShare, isApprovalFlowEnabled, isApprovedStatus, isPreExistedStatus]);

  const menuShareOptions = useMemo(() => {
    return shareAllowed && !isExternallyEdited &&
      !draft &&
      !isVideoProcessing
  }, [shareAllowed, isExternallyEdited, draft, isVideoProcessing]);

  const menuOptions = useMemo(() => {
    const options = [
      {
        id: 'move',
        label: t('general.video_action_button.move'),
        action: () => {
          setShowShareMenu({});
          moveVideo(video.id, video.name, video.folder);
        },
        visible: Boolean(moveVideo),
      },
      {
        id: 'copy_link',
        label: t('general.video_action_button.copy_link'),
        action: () => {
          setShowShareMenu({});
          handleCopyClick(video.id);
        },
        visible: menuShareOptions,
      },
      {
        id: 'share',
        label: t('general.video_action_button.share'),
        action: () => {
          setShowShareMenu({});
          shareVideo(video.id);
        },
        visible: menuShareOptions,
      },
      {
        id: 'download',
        label: t('general.video_action_button.download'),
        action: () => {
          setShowShareMenu({});
          handleDownload();
        },
        visible: (sharedMode ? canDownload : !isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) &&
          !draft &&
          !isVideoProcessing,
        disabled: isProtected && !isUserHasEditingRights
      },
      {
        id: 'duplicate',
        label: t('general.video_action_button.duplicate'),
        action: () => {
          setShowShareMenu({});
          handleDuplicate(video.id, video.name);
        },
        visible: duplicateVideo && !isVideoProcessing,
      },
      {
        id: 'archive',
        label: t('general.video_action_button.archive'),
        action: () => {
          setShowShareMenu({});
          archiveVideo(video.id, video.name);
        },
        visible: Boolean(archiveVideo),
      },
      {
        id: 'delete',
        label: t('general.video_action_button.delete'),
        action: () => {
          setShowShareMenu({});
          deleteVideo(video.id, video.name);
        },
        visible: Boolean(deleteVideo),
      }
    ];
    return options.filter(option => option.visible);
  }, [
    t,
    video.id,
    video.name,
    video.folder,
    moveVideo,
    handleCopyClick,
    shareVideo,
    handleDownload,
    isProtected,
    isUserHasEditingRights,
    duplicateVideo,
    isVideoProcessing,
    archiveVideo,
    deleteVideo,
    menuShareOptions,
    sharedMode,
    canDownload,
    isApprovalFlowEnabled,
    isApprovedStatus,
    isPreExistedStatus
  ]);

  const isShowApprovedDescription = useMemo(
    () => (video?.reviewed_at ? new Date() - new Date(video.reviewed_at) < 10 * 24 * 60 * 1000 : true),
    [video]
  );

  const shareTooltipInfo = useMemo(() => {
    if (isVideoProcessing) {
      return t('my_video.tools.processing_tooltip');
    }

    if (draft) {
      return t('my_video.tools.draft_tooltip');
    }

    if (isApprovalFlowEnabled && !isApprovedStatus && !isPreExistedStatus) {
      return t('general.video_action_button.share.tooltip');
    }

    if (isExternallyEdited) {
      return t('my_video.tools.externally_edited_tooltip');
    }

    return null;
  }, [isApprovalFlowEnabled, isApprovedStatus, isPreExistedStatus, isVideoProcessing, isExternallyEdited, draft]);

  const isNameEllipsis = useMemo(() => {
    const deviation = 5; // value in px
    return nameRef.current?.scrollHeight - nameRef.current?.offsetHeight > deviation;
  }, [video.name, nameRef.current?.scrollHeight]);

  const isAnalyticsNotEmpty = useMemo(() => analyticsInfo?.cta?.length || analyticsInfo?.views?.length, [analyticsInfo]);

  useOutsideClick(analyticsMenuRef, () => showAnalyticsInfo && setShowAnalyticsInfo(false));

  useOutsideClick(shareMenuRef, () => {
    if (Object.keys(showShareMenu).length) {
      setShowShareMenu({});
    }
  });

  const handleDownload = useCallback(() => {
    if (isExternallyEdited && !sharedMode) {
      setShowChooseSourceModal('download');
      return;
    }
    downloadVideo();
  }, [isExternallyEdited, sharedMode])

  const handleDuplicate = useCallback(() => {
    if (sharedMode) {
      return;
    }
    if (isExternallyEdited) {
      setShowChooseSourceModal('duplicate');
    } else {
      duplicateVideo(video.id, video.name);
    }
  }, [isExternallyEdited, sharedMode, video])

  const handleSubmitModal = useCallback(() => {
    if (showChooseSourceModal === 'download') {
      downloadVideo(sourceMode === EDITED ? null : 'original');
    } else {
      duplicateVideo(video.id, video.name, sourceMode === EDITED ? null : 'original');
    }
    setShowChooseSourceModal(null);
  }, [showChooseSourceModal, sourceMode])

  const handleOpenMenu = (e, id) => {
    setShowShareMenu({
      id: id,
      flag: true,
      position: { top: e.clientY, left: e.clientX - 100 },
    });
  };

  const handleCloseSourceModal = () => {
    setShowChooseSourceModal(null);
    setSourceMode(EDITED)
  }

  const handleMouseOver = useCallback(() => {
    isProtected && setShowDownloadMessage(true);
  }, [isProtected]);

  const handleMouseLeave = useCallback(() => {
    isProtected && setShowDownloadMessage(false);
  }, [isProtected]);

  useEffect(() => {
    let progress = downloadProgress.progress ?? false;
    if (progress && !toastId.current) {
      toastId.current = ToastNotification({
        isMobileView: isMobileView,
        toastId: 'download',
        Child: <DownloadUI progress={progress} videoName={video.name} />,
        onClose: () => (toastId.current = null),
      });
    } else {
      toast.update('download', {
        render: <DownloadUI progress={progress} videoName={video.name} />,
        autoClose: progress >= 100,
        closeOnClick: progress >= 100 ? 3500 : false,
      });
    }
  }, [downloadProgress]);

  const setImageOrientation = (setIsPortrait, imageUrl) => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      setIsPortrait(img.height > img.width);
    };
  };

  useEffect(() => {
    companyLogo && setImageOrientation(setIsPortrait, companyLogo);
  }, [companyLogo]);

  const handleOpenAnalyticsInfoModal = () => {
    setShowAnalyticsInfo(true);
  };

  const handleCloseAnalyticsInfoModal = () => {
    setShowAnalyticsInfo(false);
  };

  return (
    <div
      className={cx(styles.videoHeaderContainer, {
        [styles.noLogo]: !isActiveLogo && !sharedMode && (isApprovalFlowEnabled ? isPreExistedStatus || isReviewerUser : true),
      })}
    >
      <div className={styles.titleContainer}>
        {editable?.isEdit ? (
          <input
            className={styles.editableTitle}
            ref={editableRef}
            type="text"
            id={video.id}
            defaultValue={editable.name}
            onChange={handleNameChange}
            size={size}
            maxLength={MAX_NAME_LENGTH}
            autoFocus
          />
        ) : (
          <>
            <h1
              ref={nameRef}
              className={styles.title}
              onMouseEnter={() => setIsNameHover(true)}
              onMouseLeave={() => setIsNameHover(false)}
            >
              {video && video.name}
            </h1>
            {isNameEllipsis && (
              <Tooltip text={video && video.name} isHover={isNameHover} moveable={false} classNameTooltip={styles.fullVideoName} />
            )}
            {!isVideoProcessing &&
              !isMobileView &&
              ((isApprovalFlowEnabled && isReviewerUser && isInReviewStatus) ||
                ((!isApprovalFlowEnabled || (!isApprovedStatus && !isInReviewStatus)) && isUserHasEditingRights)) && (
                <Icon className={styles.pencilIcon} src="pencil" onClick={onEditClick} />
              )}
          </>
        )}
        {isMobileView && (
          <div className={styles.mobileDotsMenu}>
            <DotsBtn tagName="BUTTON" onClick={handleOpenMobileMenu} disabled={isVideoProcessing} />
          </div>
        )}
      </div>
      {!sharedMode && isApprovalFlowEnabled && !isReviewerUser && isConceptStatus && (
        <HoverExpandText status={t('general.video_action_button.submit_for_review')} description={t('my_video.tools.review.status_description.concept')} />
      )}
      {!sharedMode && isApprovalFlowEnabled && !isReviewerUser && isInReviewStatus && (
        <HoverExpandText status={t('review.status.in_review')} description={t('my_video.tools.review.status_description.in_review')} />
      )}
      {!sharedMode && isApprovalFlowEnabled && !isReviewerUser && isDeclinedStatus && (
        <HoverExpandText status={t('review.status.decline')} description={t('my_video.tools.review.status_description.declined')} />
      )}
      {!sharedMode && isApprovalFlowEnabled && !isReviewerUser && isApprovedStatus && isShowApprovedDescription && (
        <HoverExpandText status={t('review.status.approved')} description={t('my_video.tools.review.status_description.approved_video')} className={cx({
          [styles.statusHideBackground]: !isShowApprovedDescription,
        })} />
      )}
      <div className={styles.shareContainer}>
        {(!sharedMode || canViewAnalytics || isUserHasEditingRights)
          ? (
            isVideoAuthor && isAnalyticsNotEmpty && isViewAnalyticsEnabled
              ? (
                <div className={styles.viewsContainer} onClick={handleOpenAnalyticsInfoModal}>
                  <span className={styles.viewsText}>{t('analytics.title')}</span>
                </div>
              )
              : (
                <div className={cx(styles.viewsContainer, styles.emptyViewsContainer)}>
                  <span className={styles.viewsText}>
                    {video?.stats?.views || 0} {video?.stats?.views === 1 ? t('general.view') : t('general.views')}
                  </span>
                </div>
              )
          )
          : null
        }
        {!isMobileView && showAnalyticsInfo ? (
          <AnalyticsInfo
            inputRef={analyticsMenuRef}
            info={analyticsInfo}
            onClose={handleCloseAnalyticsInfoModal}
            className={sharedMode && styles.analyticsInfo}
          />
        ) : null}
        {shareAllowed ?
          <CustomButtonTooltip
            label={t('general.video_action_button.share')}
            variant={'secondary'}
            onClick={() => shareVideo(video.id)}
            customStyles={{ marginRight: '20px' }}
            disabled={(isApprovalFlowEnabled && !isApprovedStatus && !isPreExistedStatus) || draft || isVideoProcessing}
            tooltip={shareTooltipInfo}
            tooltipClassName={styles.shareTooltip}
          />
          : null}
        {shareAllowed ? (
          <CustomButtonTooltip
            variant="link"
            className={styles.sourceBtn}
            onClick={() => handleCopyClick(video.id)}
            disabled={draft}
            tooltip={shareTooltipInfo}
            tooltipClassName={cx(styles.shareTooltip, styles.shareLinkTooltip)}
            ariaLabel={t('general.video_action_button.link_copied')}
          >
            <Icon src="link" verticalAlign="middle" className={styles.sourceIcon} />
          </CustomButtonTooltip>
        ) : null}
        {isApprovalFlowEnabled && (isConceptStatus || isDeclinedStatus) && isUserHasEditingRights && (
          <CustomButton
            label={t('general.video_action_button.submit_for_review')}
            variant={'secondary'}
            disabled={isExternallyEdited || isVideoProcessing}
            onClick={openSubmitForReviewModal}
            className={styles.submitReviewBtn}
          />
        )}
        {sharedMode && canDownload ? (
          <CustomButton
            variant="link"
            className={cx(styles.downloadBtn, { [styles.disableDownload]: isProtected })}
            disabled={isProtected}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            onClick={handleDownload}
            ariaLabel={t('general.video_action_button.download')}
          >
            <Icon src="download" verticalAlign="middle" className={styles.downloadIcon} />
          </CustomButton>
        ) : null}
        {showDownloadMessage && downloadNotification?.length ? <Hint className={styles.notificationMessage} text={downloadNotification} /> : null}
        {(!sharedMode || (canDownload || canShare)) ? (
          <div className={styles.dotBtnContainer}>
            <DotsBtn tagName="BUTTON" onClick={e => handleOpenMenu(e, video.id)} />
            {(showShareMenu.id === video.id && showShareMenu.flag) ? (
              <Menu ref={shareMenuRef} listMode simple customStyles={{ ...showShareMenu.position, position: 'fixed' }}>
                {menuOptions.map(option => (
                  <MenuItem
                    key={option.id}
                    simple
                    onClick={option.action}
                    disabled={option?.disabled ?? false}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
            ) : null}
          </div>
        ) : null}
      </div>
      <Modal open={showChooseSourceModal !== null} onClose={handleCloseSourceModal}>
        <div className={styles.chooseSourceContent}>
          <div className={styles.headerContainer}>
            <h2 className={styles.title}>{t('my_video.tools.externally_edited.choose_source')}</h2>
            <CloseButton onClose={handleCloseSourceModal} />
          </div>
          <p className={styles.description}>{t('my_video.tools.externally_edited.choose_source.description', { action: t(showChooseSourceModal === 'download' ? 'general.video_action_button.download' : 'general.video_action_button.duplicate').toLowerCase() })}</p>
          <div className={styles.chooseSourceButtons}>
            <RadioButton className={cx(styles.radioButton, { [styles.selected]: sourceMode === ORIGINAL })} variant='primary' type='radiobutton' checked={sourceMode === ORIGINAL} label={t(VIDEO_VERSION[ORIGINAL])} onChange={() => setSourceMode(ORIGINAL)} />
            <RadioButton className={cx(styles.radioButton, { [styles.selected]: sourceMode === EDITED })} variant='primary' type='radiobutton' checked={sourceMode === EDITED} label={t(VIDEO_VERSION[EDITED])} onChange={() => setSourceMode(EDITED)} />
          </div>
          <div className={styles.footer}>
            <CustomButton label={t('storyboard.steps.submit')} variant='primary' onClick={handleSubmitModal} />
          </div>
        </div>
      </Modal>
    </div>
  );
};
